// src/services/dashboard.service.js
import { http } from "../lib/http";
import { getUser } from "../lib/authToken";
import { DEV_USER } from "../config/localAuth";
import { getGranularity } from "../utils/rangeUtils"; // 👈 new import


/** ----------------- L1: KPIs + TopCategories + OrderTypes ----------------- */
export async function fetchDashboardL1({
  orgId,
  location,
  range = "1m",
}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  console.log("➡️ fetchDashboardL1 called with:", { orgId, location, range });

  const qs = new URLSearchParams({ orgId, location, range_str: range });
  console.log("🔗 Final API URL:", `/fe/dashboard/l1?${qs.toString()}`);

  const dto = await http.get(`/fe/dashboard/l1?${qs.toString()}`);
  console.log("📦 API Response (dto):", dto);

  return mapL1ToUI(dto);
}


function mapL1ToUI(dto) {
  // KPIs
  const k = dto?.kpis ?? {};
  const kpis = {
    orders: {
      value: +k?.totalOrders?.value || 0,
      deltaPct: +k?.totalOrders?.deltaPct || 0,
    },
    customers: {
      value: +k?.totalCustomers?.value || 0,
      deltaPct: +k?.totalCustomers?.deltaPct || 0,
    },
    revenue: {
      value: +k?.totalRevenue?.value || 0,
      deltaPct: +k?.totalRevenue?.deltaPct || 0,
      currency: k?.totalRevenue?.currency || dto?.meta?.currency || "INR",
    },
  };

  // Top categories → donut expects {label, seriesData:[{name, value(pct)}]}
  const topCategories = {
    label: dto?.meta?.period?.label || dto?.meta?.granularity || "",
    seriesData: (dto?.topCategories || []).map((c) => ({
      name: c?.name || "Others",
      value: +(c?.percent ?? 0),
    })),
  };

  // Order types → list expects {label, items:[{label, value}]}
  const orderTypes = {
    label: "This period",
    items: (dto?.orderTypes || []).map((o) => ({
      label: o?.type || "",
      value: +o?.count || 0,
    })),
  };

  return { kpis, topCategories, orderTypes };
}

/** ----------------- L2: Revenue line ----------------- */
export async function fetchRevenueTrendL2({
  orgId,
  location,
  range = "this_month",
}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  const granularity = getGranularity(range); // 👈 auto-select
  const qs = new URLSearchParams({ orgId, location, range, granularity });

  console.log("📊 Fetching L2 with:", { orgId, location, range, granularity });

  const dto = await http.get(`/fe/dashboard/L2?${qs.toString()}`);
  return mapL2ToLine(dto);
}

// be tolerant to a few possible shapes
function mapL2ToLine(dto) {
  // If API already matches chart shape, passthrough
  if (dto?.categories && dto?.series) {
    return { categories: dto.categories, series: dto.series };
  }

  // ✅ NEW: your L2 shape → { meta, series: [{label, period, revenue}] }
  if (Array.isArray(dto?.series) && dto.series.length) {
    const categories = dto.series.map(p => p.label ?? p.period ?? "");
    const data = dto.series.map(p => Number(p.revenue) || 0);
    const name = dto?.meta?.seriesLabel || "Income";
    return {
      categories,
      series: [{ name, data }],
    };
  }

  // Legacy fallbacks (keep just in case)
  const buckets = dto?.buckets || dto?.points || dto?.data || [];
  const categories = buckets.map(b => b.label ?? b.date ?? "");
  const values = buckets.map(b => +b.revenue || +b.value || 0);

  return {
    categories,
    series: [{ name: "Income", data: values }],
  };
}

/** GET /fe/dashboard/L3 */
export async function getDashboardL3({
  orgId,
  location,
  range = "1y",
  granularity = "monthly",
}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  // Override granularity to "daily" for L3 when range is "this_month"
  if (range === "this_month") {
    granularity = "daily";
  }
  const qs = new URLSearchParams({ orgId, location, range, granularity });
  const dto = await http.get(`/fe/dashboard/L3?${qs.toString()}`);
  return mapL3ToBars(dto);
}

/** POST /fe/payments/dashboard */
export async function fetchPaymentsData({
  orgId,
  location,
  from_date,
  to_date,
  method = "All",
  status = "All",
}) {
  const user = getUser() || DEV_USER;
  orgId = orgId || user.restaurant_id;
  location = location || user.location;
  console.log("➡️ fetchPaymentsData called with:", { orgId, location, from_date, to_date, method, status });

  const payload = {
    org_id: orgId,
    location,
    from_date,
    to_date,
    method,
    status,
  };
  console.log("📤 API Payload:", payload);

  const dto = await http.post(`/fe/payments/dashboard`, payload);
  console.log("📦 API Response (dto):", dto);

  return mapPaymentsToUI(dto);
}

function mapPaymentsToUI(dto) {
  // Summary
  const summary = dto?.summary || {};
  const totals = {
    total: summary.total || 0,
    completed: summary.completed || 0,
    pending: summary.pending || 0,
  };

  // Method breakdown
  const methodBreakdown = dto?.method_breakdown || {};
  const methodAgg = Object.entries(methodBreakdown).map(([method, total]) => ({ method, total }));

  // Monthly stats
  const monthlyStats = dto?.monthly_stats || [];
  const months = monthlyStats.map(stat => `${stat.month_year}`);
  const success = monthlyStats.map(stat => stat.success);
  const failed = monthlyStats.map(stat => stat.failed);

  // Invoices
  const invoices = dto?.invoices || [];
  const rows = invoices.map(inv => ({
    id: inv.invoice_id,
    customer: inv.customer,
    method: inv.method,
    status: inv.status,
    amount: inv.amount,
    date: inv.date, // Assuming date is in 'dd MMM yyyy' format, parse if needed
  }));

  return { totals, methodAgg, months, success, failed, rows };
}

/** Normalize L3 response to {categories, series:[{name:'Income', data:[]}], label?} */
export function mapL3ToBars(dto) {
  if (dto?.categories && dto?.series) return dto;

  if (Array.isArray(dto?.series)) {
    const categories = dto.series.map((p) => p.label ?? p.period ?? "");
    const values = dto.series.map((p) =>
      Number(p.revenue ?? p.orders ?? p.count ?? p.value ?? 0)
    );
    return {
      label:
        dto?.meta?.label ??
        dto?.meta?.range ??
        `${dto?.meta?.granularity || ""}`.trim(),
      categories,
      series: [{ name: dto?.meta?.seriesLabel || "Income", data: values }],
    };
  }

  // Fallbacks (buckets/points/data)
  const buckets = dto?.buckets || dto?.points || dto?.data || [];
  const categories = buckets.map((b) => b.label ?? b.date ?? "");
  const values = buckets.map((b) => Number(b.revenue ?? b.count ?? b.orders ?? b.value ?? 0));
  return {
    label: dto?.meta?.label || "",
    categories,
    series: [{ name: dto?.meta?.seriesLabel || "Income", data: values }],
  };
}

export const fetchPaymentsReport = (payload) => {
  return http.post('/fe/payment/reports', payload, {
    responseType: 'blob', // important for file download
  });
};
